import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Input, Row, Switch, InputNumber, Card, Select, DatePicker } from 'antd';
import '../styles.css';
import { useCallback, useMemo } from 'react';
import { ISunkhronosModule, ISunkhronosModuleSetting } from '../utils';
import FAIcon from '../../../../common/FAIcon/FAIcon';
import InformationBadge from '../../../../common/InformationBadge/InformationBadge';

interface Props {
    companyType: number;
    modules: ISunkhronosModule[];
    updateData: (modules: ISunkhronosModule[]) => void;
}

const ModulesCompanyDrawer = (props: Props) => {
    const intl = useIntl();

    const handleValueChange = useCallback((moduleKey: string, settingKey: string, value: any) => {
        const updateSettings = (settings: any[]): any[] =>
            settings.map(setting => {
                if (setting.key === settingKey) {
                    return { ...setting, value };
                } else if (setting.settings) {
                    return { ...setting, settings: updateSettings(setting.settings) };
                }
                return setting;
            });

        props.updateData(
            props.modules.map(module =>
                module.key === moduleKey
                    ? { ...module, settings: updateSettings(module.settings) }
                    : module
            )
        );
    }, [props]);


    const toggleModuleEnabled = useCallback((key: string) => {
        props.updateData(
            props.modules.map(module =>
                module.key === key ? { ...module, enabled: !module.enabled } : module
            )
        );
    }, [props]);

    const renderSetting = useCallback((
        module: ISunkhronosModule,
        setting: ISunkhronosModuleSetting,
        parentEnabled: boolean = true,
        depth: number = 0
    ) => {
        const isEnabled = parentEnabled && (setting.type !== 'bool' || setting.value === true);

        return (
            <div
                key={setting.key}
                style={{
                    marginBottom: '1em',
                    marginLeft: depth * 16,
                    paddingLeft: depth > 0 ? 12 : 0,
                    borderLeft: depth > 0 ? '2px solid #ddd' : 'none',
                    opacity: parentEnabled ? 1 : 0.5,
                }}

            >
                <div className='flex-between centerHeight'>
                    <label>{intl.formatMessage(setting.label)}</label>
                    {setting.type === 'bool' ? (
                        <Switch
                            size='small'
                            checked={setting.value as boolean}
                            onChange={checked => handleValueChange(module.key, setting.key, checked)}
                            disabled={!parentEnabled}
                        />
                    ) : setting.type === 'number' ? (
                        <InputNumber
                            value={setting.value as number}
                            onChange={value => handleValueChange(module.key, setting.key, value)}
                            disabled={!parentEnabled}
                        />
                    ) : setting.type === 'string' ? (
                        <Input
                            value={setting.value as string}
                            onChange={e => handleValueChange(module.key, setting.key, e.target.value)}
                            disabled={!parentEnabled}
                        />
                    ) : setting.type === 'select' ? (
                        <Select
                            value={setting.value}
                            onChange={(value) => handleValueChange(module.key, setting.key, value)}
                            style={{ width: 150, marginRight: "3px" }}
                            options={setting.options.map((option) => { return { label: intl.formatMessage(option.label), value: option.value }; })}
                            disabled={!parentEnabled}
                        />
                    ) : setting.type === 'date' ?
                        (
                            <InformationBadge content={setting.information}>
                                <DatePicker
                                    value={setting.value}
                                    onChange={(value) => handleValueChange(module.key, setting.key, value)}
                                    placeholder={setting.placeholder ? intl.formatMessage(setting.placeholder) : undefined}
                                    disabled={!parentEnabled}
                                    prefix={setting.prefix}
                                />
                            </InformationBadge>
                        ) : null}
                </div>

                {Array.isArray(setting.settings) &&
                    setting.settings.map((subSetting: any) =>
                        renderSetting(module, subSetting, isEnabled, depth + 1)
                    )}
            </div>
        );
    }, [handleValueChange, intl]);

    const filteredModules = useMemo(() => {
        return props.modules.filter(module => module.companyTypes?.includes(props.companyType));
    }, [props.modules, props.companyType]);

    return (
        <Row gutter={[10, 10]}>
            <Col xs={{ span: 24 }}>
                <h2><FAIcon prefix='fad' name='grid-2-plus' /> <FormattedMessage defaultMessage="Modules" /></h2>
                {
                    filteredModules.map(module => (
                        <Card
                            key={module.key}
                            title={
                                <div className='flex-between centerHeight'>
                                    <span><FAIcon prefix='fad' name={module.icon ?? ""} /> {intl.formatMessage(module.nameSection)}</span>
                                    {
                                        module.showSwitch !== false ? (
                                            <Switch
                                                size='small'
                                                checked={module.enabled}
                                                onChange={() => toggleModuleEnabled(module.key)}
                                            />
                                        ) : null
                                    }
                                </div>
                            }
                            className='card'
                        >
                            {
                                module.settings.length > 0 ? (
                                    <div className='card-content'>
                                        {

                                            module.settings.map(setting =>
                                                renderSetting(module, setting, module.enabled)
                                            )
                                        }
                                    </div>
                                ) : null
                            }
                        </Card>
                    ))
                }


            </Col>
        </Row>
    );
};

export default ModulesCompanyDrawer;
